import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaUser, FaLock } from "react-icons/fa";
import { useAuth } from "../contexts/AuthContext";
import lightBackground from "../light.jpg";
import darkBackground from "../dark.jpg";

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login, isAuthenticated, loading: authLoading } = useAuth();

  // Redirect if already authenticated
  useEffect(() => {
    if (!authLoading && isAuthenticated()) {
      navigate("/dashboard", { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px',
        color: 'var(--text-secondary, #666)',
        background: 'var(--bg-primary, #f8f9fd)'
      }}>
        Loading...
      </div>
    );
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    const result = await login(username, password);

    if (result.success) {
      navigate("/dashboard", { replace: true });
    } else {
      setError(result.error || "An error occurred during login");
    }

    setLoading(false);
  };


  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-logo-container">
          <img src="/logo.png" alt="Ecohomely Logo" className="login-logo" />
        </div>
        <h2 className="login-title">Login</h2>
        <form onSubmit={handleLogin} className="login-form">
          <div className="input-group">
            <FaUser className="input-icon" />
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Username"
              required
              className="login-input"
              disabled={loading}
            />
          </div>
          <div className="input-group">
            <FaLock className="input-icon" />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              required
              className="login-input"
              disabled={loading}
            />
          </div>
          {error && <p className="error-message">{error}</p>}
          <button type="submit" className="login-button" disabled={loading}>
            {loading ? (
              <span className="loader"></span>
            ) : (
              "Login"
            )}
          </button>
        </form>
      </div>

      <style>{`
        .login-container {
          min-height: 100vh;
          width: 100vw;
          display: flex;
          align-items: center;
          justify-content: center;
          background-image: url('${lightBackground}');
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
          background-attachment: fixed;
          background-color: #56ccf2; /* Fallback color */
          padding: 2rem;
          transition: background-image 0.3s ease;
          position: relative;
          margin: 0;
          overflow: hidden;
        }

        /* Overlay for transparency effect on login container */
        .login-container::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, rgba(86, 204, 242, 0.5), rgba(47, 128, 237, 0.5));
          z-index: 0;
          pointer-events: none;
        }

        /* Ensure login card appears above overlay */
        .login-card {
          position: relative;
          z-index: 1;
        }

        [data-theme="dark"] .login-container {
          background-image: url('${darkBackground}');
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
          background-attachment: fixed;
          background-color: #1a1a2e; /* Fallback color */
        }

        [data-theme="dark"] .login-container::before {
          background: linear-gradient(135deg, rgba(26, 26, 46, 0.5), rgba(22, 33, 62, 0.5));
        }

        .login-card {
          background: var(--bg-card, #ffffff);
          border-radius: 16px;
          padding: 3rem;
          width: 100%;
          max-width: 400px;
          box-shadow: 0 10px 25px var(--shadow, rgba(0, 0, 0, 0.1));
          transition: transform 0.3s ease, background 0.3s ease, box-shadow 0.3s ease;
        }

        .login-card:hover {
          transform: translateY(-5px);
        }

        .login-logo-container {
          display: flex;
          justify-content: center;
          align-items: center;
          margin-bottom: 1.5rem;
        }

        .login-logo {
          max-width: 100px;
          max-height: 100px;
          width: auto;
          height: auto;
          object-fit: contain;
        }

        .login-title {
          font-size: 2rem;
          font-weight: 700;
          color: var(--text-primary, #1f2937);
          text-align: center;
          margin-bottom: 2rem;
          background: linear-gradient(to right, var(--text-primary, #1f2937), var(--text-secondary, #4b5563));
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
        }

        [data-theme="dark"] .login-title {
          background: linear-gradient(to right, var(--text-primary, #e5e5e5), var(--text-secondary, #b0b0b0));
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
        }

        .login-form {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .input-group {
          position: relative;
          display: flex;
          align-items: center;
        }

        .input-icon {
          position: absolute;
          left: 1rem;
          color: var(--text-muted, #6b7280);
          font-size: 1.25rem;
          transition: color 0.3s ease;
        }

        .login-input {
          width: 100%;
          padding: 1rem 1rem 1rem 3rem;
          border: 2px solid var(--border-color, #e5e7eb);
          border-radius: 10px;
          font-size: 1rem;
          background: var(--bg-input, #ffffff);
          color: var(--text-primary, #1f2937);
          transition: all 0.3s ease;
        }

        .login-input:focus {
          border-color: #3b82f6;
          outline: none;
          box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
        }

        .login-input::placeholder {
          color: var(--text-muted, #9ca3af);
        }

        .login-input:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .error-message {
          color: #dc2626;
          font-size: 0.9rem;
          text-align: center;
          margin: 0;
          background: #fef2f2;
          padding: 0.5rem;
          border-radius: 8px;
          border: 1px solid #fecaca;
        }

        [data-theme="dark"] .error-message {
          background: rgba(220, 38, 38, 0.2);
          border-color: rgba(220, 38, 38, 0.4);
          color: #fca5a5;
        }

        .login-button {
          padding: 1rem;
          background: linear-gradient(90deg, #3b82f6, #2563eb);
          color: white;
          border: none;
          border-radius: 10px;
          font-size: 1.1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .login-button:hover:not(:disabled) {
          background: linear-gradient(90deg, #2563eb, #1d4ed8);
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(59, 130, 246, 0.3);
        }

        .login-button:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .loader {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          display: inline-block;
          position: relative;
          border: 2px solid #FF3D00;
          box-sizing: border-box;
          animation: rotation 1s linear infinite;
        }
        .loader::after {
          content: '';  
          box-sizing: border-box;
          position: absolute;
          left: 2px;
          top: 2px;
          border: 2px solid #000;
          width: 6px;
          height: 6px;
          border-radius: 50%;
        }
        [data-theme="dark"] .loader::after {
          border: 2px solid #FFF;
        }
        @keyframes rotation {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default Login;